/**
 * Variant selectors + Add to Cart (Phase 5) + pricing matrix / spec summary (Phase 15).
 */

/**
 * @param {HTMLElement} root
 * @param {{ setState: (patch: Record<string, unknown>) => void, getState: () => Record<string, unknown>, subscribe: (fn: (state: Record<string, unknown>) => void) => void }} store
 * @param {(state: Record<string, unknown>) => Promise<void>} onAddToCart
 */
export function bindCheckoutToolbar(root, store, onAddToCart) {
    /**
     * Re-read attrs so an in-place size switch can update prices
     * without remounting the toolbar.
     * @returns {Record<string, Record<string, number>>}
     */
    function readPricingMatrix() {
        try {
            const raw = root.getAttribute('data-pricing-matrix');
            if (raw) {
                return JSON.parse(raw);
            }
        } catch {
            // keep empty
        }
        return {};
    }
    const priceLabel = root.querySelector('#bag-price-label');
    const specSummary = root.querySelector('#bag-spec-summary');
    function currentSizeLabel() {
        return (root.getAttribute('data-size-label') || '').trim();
    }
    const statusEl = root.querySelector('#bag-cart-status');
    const addBtn = root.querySelector('#bag-add-to-cart-btn');

    /**
     * @param {number} canvasOz
     * @param {string} printSides
     */
    function resolvePrice(canvasOz, printSides) {
        const ozKey = canvasOz === 12 ? '12' : '8';
        const row = readPricingMatrix()[ozKey];
        if (row && typeof row[printSides] === 'number') {
            return row[printSides];
        }
        const fallbackFrontOnly = parseFloat(root.getAttribute('data-price-front-only') || '24.99');
        const fallbackFrontBack = parseFloat(root.getAttribute('data-price-front-back') || '29.99');
        return printSides === 'front_back' ? fallbackFrontBack : fallbackFrontOnly;
    }

    /**
     * @param {number} canvasOz
     * @param {string} printSides
     */
    function updateSpecSummary(canvasOz, printSides) {
        if (!specSummary) {
            return;
        }
        const sidesText = printSides === 'front_back' ? 'Front + Back' : 'Front Only';
        const price = resolvePrice(canvasOz, printSides).toFixed(2);
        const parts = [];
        const sizeLabel = currentSizeLabel();
        if (sizeLabel) {
            parts.push(sizeLabel);
        }
        parts.push(canvasOz + 'oz');
        parts.push(sidesText);
        parts.push('$' + price);
        specSummary.textContent = parts.join(' · ');
    }

    root.querySelectorAll('[data-bag-canvas]').forEach((btn) => {
        btn.addEventListener('click', () => {
            if (store.getState().busy) {
                return;
            }
            const oz = parseInt(btn.getAttribute('data-bag-canvas') || '8', 10);
            store.setState({ canvasOz: oz === 12 ? 12 : 8 });
        });
    });

    root.querySelectorAll('[data-bag-print]').forEach((btn) => {
        btn.addEventListener('click', () => {
            if (store.getState().busy) {
                return;
            }
            const sides = btn.getAttribute('data-bag-print');
            if (sides !== 'front_only' && sides !== 'front_back') {
                return;
            }
            // print_sides is a cart variant only — do not fight Product-view
            // rotate presets that set activeSide to back (Phase 9.5).
            store.setState({ printSides: sides });
        });
    });

    if (addBtn) {
        addBtn.addEventListener('click', async () => {
            if (store.getState().busy) {
                return;
            }
            addBtn.disabled = true;
            if (statusEl) {
                statusEl.textContent = 'Adding to cart…';
            }
            try {
                await onAddToCart(store.getState());
                if (statusEl) {
                    statusEl.textContent = 'Added to cart. You can keep designing or go to checkout.';
                }
            } catch (err) {
                if (statusEl) {
                    statusEl.textContent = err && err.message ? err.message : 'Could not add to cart.';
                }
            } finally {
                addBtn.disabled = Boolean(store.getState().busy);
            }
        });
    }

    store.subscribe((state) => {
        const canvasOz = state.canvasOz === 12 ? 12 : 8;
        const printSides = state.printSides === 'front_back' ? 'front_back' : 'front_only';

        root.querySelectorAll('[data-bag-canvas]').forEach((btn) => {
            const oz = parseInt(btn.getAttribute('data-bag-canvas') || '8', 10);
            btn.classList.toggle('is-active', oz === canvasOz);
            btn.setAttribute('aria-pressed', oz === canvasOz ? 'true' : 'false');
            if (btn instanceof HTMLButtonElement) {
                btn.disabled = Boolean(state.busy);
            }
        });

        root.querySelectorAll('[data-bag-print]').forEach((btn) => {
            const sides = btn.getAttribute('data-bag-print');
            btn.classList.toggle('is-active', sides === printSides);
            btn.setAttribute('aria-pressed', sides === printSides ? 'true' : 'false');
            if (btn instanceof HTMLButtonElement) {
                btn.disabled = Boolean(state.busy);
            }
        });

        // Keep Front/Back pills visible for Product-view angle sync (doc/bag/07).
        // Hiding/forcing front when printSides=front_only made "Drag to rotate" a no-op.

        if (priceLabel) {
            priceLabel.textContent = '$' + resolvePrice(canvasOz, printSides).toFixed(2);
        }
        updateSpecSummary(canvasOz, printSides);
        if (addBtn) {
            addBtn.disabled = Boolean(state.busy);
        }
    });
}
